import {createStyles} from '@mantine/core';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

const STROKE_WIDTH = 2;
const HALF_STROKE = STROKE_WIDTH / 2;
const LINE_FRACTION = 0.15;
const SPIN_DURATION_MS = 2500;

function computePerimeter(width: number, height: number, rx: number): number {
  const rw = width - STROKE_WIDTH;
  const rh = height - STROKE_WIDTH;
  return 2 * (rw + rh) - 8 * rx + 2 * Math.PI * rx;
}

const useStyles = createStyles(() => ({
  svg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 10,
  },
  '@keyframes omniboxSvgFade': {
    '0%': {opacity: 1},
    '100%': {opacity: 0},
  },
  fadeOut: {
    animation: '$omniboxSvgFade 600ms ease-out forwards',
  },
}));

export default function AnimatedBorderOverlay({
  loading,
  showCompletionFlash,
}: {
  loading: boolean;
  showCompletionFlash: boolean;
}): JSX.Element | null {
  const {classes} = useStyles();
  const portalRef = useRef<HTMLDivElement | null>(null);
  const rectRef = useRef<SVGRectElement | null>(null);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number | null>(null);
  const sizeRef = useRef<{width: number; height: number} | null>(null);
  const borderRadiusRef = useRef(8);
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  const [size, setSize] = useState<{width: number; height: number} | null>(null);
  const [borderRadius, setBorderRadius] = useState(8);

  // Keep a ref in sync so the rAF loop can read the latest size without restarting
  sizeRef.current = size;

  useLayoutEffect(() => {
    const findContent = (): HTMLElement | null => {
      const inner = document.querySelector('[data-omnibox-content]');
      if (!inner) {
        return null;
      }
      // Walk up to the Modal's content wrapper (the element with border-radius)
      return inner.closest('.mantine-Modal-content');
    };

    let el = findContent();
    if (!el) {
      const frame = requestAnimationFrame(() => {
        el = findContent();
        if (el) {
          setup(el);
        }
      });
      return () => {
        cancelAnimationFrame(frame);
      };
    }
    return setup(el);

    function setup(contentElement: HTMLElement): () => void {
      const prevPosition = contentElement.style.position;
      const prevOverflow = contentElement.style.overflow;
      contentElement.style.position = 'relative';
      contentElement.style.overflow = 'hidden';

      const computedRadius = parseFloat(getComputedStyle(contentElement).borderRadius) || 8;
      const rx = computedRadius - HALF_STROKE;
      borderRadiusRef.current = rx;
      setBorderRadius(rx);

      if (!portalRef.current) {
        portalRef.current = document.createElement('div');
      }
      if (portalRef.current.parentNode !== contentElement) {
        contentElement.appendChild(portalRef.current);
      }
      setContentEl(contentElement);

      const ro = new ResizeObserver(([entry]) => {
        const {width, height} = entry.contentRect;
        setSize({width, height});
      });
      ro.observe(contentElement);

      return () => {
        ro.disconnect();
        portalRef.current?.remove();
        contentElement.style.position = prevPosition;
        contentElement.style.overflow = prevOverflow;
      };
    }
  }, [loading, showCompletionFlash]);

  // rAF-driven animation for loading state — reads size from ref so it never restarts on resize
  useEffect(() => {
    if (!loading) {
      startTimeRef.current = null;
      return;
    }

    const animate = (timestamp: number): void => {
      if (startTimeRef.current == null) {
        startTimeRef.current = timestamp;
      }
      const s = sizeRef.current;
      const rx = borderRadiusRef.current;
      if (rectRef.current && s) {
        const perimeter = computePerimeter(s.width, s.height, rx);
        const lineLength = perimeter * LINE_FRACTION;
        const progress = ((timestamp - startTimeRef.current) % SPIN_DURATION_MS) / SPIN_DURATION_MS;
        rectRef.current.setAttribute('stroke-dasharray', `${lineLength} ${perimeter - lineLength}`);
        rectRef.current.setAttribute('stroke-dashoffset', String(-progress * perimeter));
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [loading]);

  if (!contentEl || !size || !portalRef.current) {
    return null;
  }

  const {width, height} = size;

  return createPortal(
    <svg className={classes.svg}>
      <defs>
        <linearGradient
          id="omnibox-border-grad"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={width}
          y2="0"
        >
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient
          id="omnibox-flash-grad"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={width}
          y2={height}
        >
          <stop offset="0%" stopColor="#7c3aed" />
          <stop offset="25%" stopColor="#a78bfa" />
          <stop offset="50%" stopColor="#c4b5fd" />
          <stop offset="75%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <rect
        ref={rectRef}
        x={HALF_STROKE}
        y={HALF_STROKE}
        width={width - STROKE_WIDTH}
        height={height - STROKE_WIDTH}
        rx={borderRadius}
        ry={borderRadius}
        fill="none"
        stroke={showCompletionFlash ? 'url(#omnibox-flash-grad)' : 'url(#omnibox-border-grad)'}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        className={showCompletionFlash ? classes.fadeOut : undefined}
      />
    </svg>,
    portalRef.current
  );
}
